import React, { useEffect, useRef, useState } from 'react';
import { Observer } from 'mobx-react';
import { useTheme } from '@mui/material/styles';

import { useStyles } from './GradingRecap.styles'

import { useGradingsUIStore } from '@core/useStores';

import { ThemeProps } from '@styles/theme';
import { getGradingColor, getNormalizedGradingValue, getSafetyGradeLabel, GRADES, GradingBehaviour, GradingCompetency, toSpaced } from 'NeoAnalytics/stores/GradingsUI.store';
import { BehaviourRow, toAcronym } from '../GradingEditor';
import classNames from 'classnames';


export const GradingRecap: React.FC = () =>{
    const theme = useTheme<ThemeProps>();
    const styles = useStyles({ theme });

    const gradingsUIStore = useGradingsUIStore();

    const [activeComp, setActiveComp] = useState<GradingCompetency>();
    const competencyRefs = useRef<Record<number, HTMLDivElement | null>>({});
    const bodyRef = useRef<HTMLDivElement | null>(null);
    const competencies = gradingsUIStore.groupedBehaviours?.formattedComps ?? [];


    useEffect(() =>{
        if (competencies.length === 0) {
            setActiveComp(undefined);

            return;
        }

        if (!activeComp || !competencies.some(competency => competency.id === activeComp.id)) {
            setActiveComp(competencies[0]);
        }
    }, [competencies, activeComp])

    const getGradingValues = React.useCallback((competencyId: number) => {
        return gradingsUIStore.groupedBehaviours?.gradingValues?.find(
            value => getNormalizedGradingValue(value).compId === competencyId
        );
    }, [gradingsUIStore.groupedBehaviours?.gradingValues]);

    const selectCompetency = (competency: GradingCompetency) => {
        setActiveComp(competency);

        const container = bodyRef.current;
        const target = competencyRefs.current[competency.id];
        if (!container || !target) {
            return;
        }

        // Scroll only the inner container to the target, rather than
        // scrollIntoView() which also scrolls every scrollable ancestor
        // (including the window). Position is measured relative to the
        // container's current scroll origin so it's layout-independent.
        const top = target.getBoundingClientRect().top
            - container.getBoundingClientRect().top
            + container.scrollTop;

        container.scrollTo({ top, behavior: 'smooth' });
    };

    return (
        <>
            <Observer>
                {() =>{
                    const data = competencies;

                    return(
                        <div className={styles.root}>
                            <div className={styles.header}>
                            Recap:
                                {data.map(c =>{
                                    const gradingValues = getGradingValues(c.id);
                                    const normalizedGradingValues = getNormalizedGradingValue(gradingValues);
                                    const safetyGrade = normalizedGradingValues.safetyGrade;
                                    const grade = normalizedGradingValues.grade;
                                    const averageGrade =
                                    safetyGrade != null && grade != null
                                        ? Math.round((safetyGrade + grade) / 2)
                                        : undefined;
                                    const isActive = activeComp?.id === c.id;

                                    return(
                                        <button
                                            key={c.id}
                                            type="button"
                                            className={classNames(
                                                styles.compStatus,
                                                isActive && styles.activeCompStatus
                                            )}
                                            onClick={() => selectCompetency(c)}
                                        >
                                            <div className={styles.compSquare}
                                                style={{
                                                    backgroundColor: averageGrade != null ? getGradingColor(averageGrade) : '',
                                                }}
                                            />
                                            <div className={styles.label}>
                                                {c?.acronym ? c.acronym : toAcronym(c!.title)}
                                            </div>
                                        </button>
                                    )
                                })}
                            </div>
                            <div className={styles.body} ref={bodyRef}>
                                {data.map(c => {
                                    if(c){
                                        const gradingValues = getGradingValues(c.id);
                                        const normalizedGradingValues = getNormalizedGradingValue(gradingValues);
                                        const safetyGrade = normalizedGradingValues.safetyGrade;
                                        const grade = normalizedGradingValues.grade;

                                        const note = gradingsUIStore.groupedBehaviours?.notes.find(n => n.compId == c.id);

                                        return (
                                            <div
                                                key={c.id}
                                                ref={element => {
                                                    competencyRefs.current[c.id] = element;
                                                }}
                                                className={styles.compDataWrapper}
                                            >
                                                <div className={styles.competencyWrapper}>
                                                    <div className={styles.compHeaderContainer}>
                                                        <div className={styles.compTitleContainer}>
                                                            <b>
                                                                {`${c.id}. ${c?.title}`}
                                                            </b>
                                                        </div>
                                                        {c.behaviours.length > 0 && (
                                                            <div className={styles.compCounterContainer}>
                                                                <b>
                                                                    {`${c.behaviours.filter(b => b.steps.some(s => s.obDetected)).length}`}
                                                                </b>
                                                                <p>
                                                                    {`/${c.behaviours.length}`}
                                                                </p>
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div className={styles.compDescriptionContainer}>
                                                        {c?.description ? c?.description : 'No Description'}
                                                    </div>
                                                    <div className={styles.gradingContainer}>
                                                        <b>
                                                            Threat and error managment
                                                        </b>
                                                        <div
                                                            className={classNames(
                                                                styles.gradingValueContainer,
                                                                safetyGrade == undefined && styles.noValue
                                                            )}
                                                            style={{
                                                                backgroundColor: safetyGrade != undefined ? getGradingColor(safetyGrade) : '',
                                                                color: safetyGrade != undefined ? getGradingColor(safetyGrade, true) : '',
                                                            }}
                                                        >
                                                            {safetyGrade != undefined ? getSafetyGradeLabel(safetyGrade) : "Not Set"}
                                                        </div>
                                                    </div>
                                                    <div className={styles.gradingContainer}>
                                                        <b>
                                                            Grading
                                                        </b>
                                                        <div
                                                            className={classNames(
                                                                styles.gradingValueContainer,
                                                                grade == undefined && styles.noValue
                                                            )}
                                                            style={{
                                                                backgroundColor: grade != undefined ? getGradingColor(grade) : '',
                                                                color: grade != undefined ? getGradingColor(grade, true) : '',
                                                            }}
                                                        >
                                                            {grade != undefined ? toSpaced(GRADES[grade]) : 'Not Set'}
                                                        </div>
                                                    </div>
                                                    <div className={styles.notesContainer}>
                                                        <b>
                                                            Notes
                                                        </b>
                                                        <div className={styles.textInput}>
                                                            {note ? note.text : ''}
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className={styles.behavioursContainer}>
                                                    {c?.behaviours.map((b: GradingBehaviour, i: number) =>
                                                        (<BehaviourRow key={i} b={b}/>)
                                                    )}
                                                </div>
                                            </div>
                                        )
                                    }else{
                                        return;
                                    }
                                })
                                }
                            </div>
                        </div>
                    )
                }}
            </Observer>
        </>
    )
}